import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  GraduationCap, Users, Briefcase, Bus,
  Smartphone, Share, PlusSquare, MoreVertical
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AppInstallPage = () => {
  const { schoolId, appType } = useParams();
  const navigate = useNavigate();
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSchoolInfo();
    
    // Set dynamic manifest for this school/app
    if (schoolId && appType) {
      const manifestLink = document.getElementById('app-manifest');
      if (manifestLink) {
        manifestLink.href = `${API_URL}/api/pwa/manifest/${schoolId}/${appType}`;
      }
      
      // Also update apple-touch-icon for iOS
      const appleIcon = document.querySelector('link[rel="apple-touch-icon"]');
      if (appleIcon) {
        appleIcon.href = `${API_URL}/api/pwa/icon/${schoolId}/${appType}/192`;
      }
    }
    
    // Cleanup on unmount - restore default manifest
    return () => {
      const manifestLink = document.getElementById('app-manifest');
      if (manifestLink) {
        manifestLink.href = '/manifest.json';
      }
    };
  }, [schoolId, appType]);

  const fetchSchoolInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/schools/public/${schoolId}`);
      setSchool(response.data);
    } catch (error) {
      console.error('Failed to fetch school:', error);
      setSchool({ name: schoolId?.toUpperCase() || 'School', code: schoolId });
    } finally {
      setLoading(false);
    }
  };

  const appConfig = {
    student: { name: 'Student App', icon: GraduationCap, color: 'bg-blue-500', path: '/auth/student' },
    parent: { name: 'Parent App', icon: Users, color: 'bg-orange-500', path: '/auth/parent' },
    teacher: { name: 'Teacher App', icon: Briefcase, color: 'bg-green-500', path: '/teacher-app' },
    driver: { name: 'Driver App', icon: Bus, color: 'bg-indigo-500', path: '/driver-app' }
  };

  const config = appConfig[appType] || appConfig.student;
  const AppIcon = config.icon;

  const goToApp = () => {
    navigate(`${config.path}?school=${schoolId}`);
  };

  // Detect device
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  
  // Get school logo URL
  const schoolLogoUrl = school?.logo_url || school?.logo;
  const hasLogo = !!schoolLogoUrl;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className={`${config.color} text-white py-12 px-4 text-center`}>
        <div className="w-20 h-20 bg-white rounded-2xl mx-auto mb-4 flex items-center justify-center overflow-hidden shadow-lg">
          {hasLogo ? (
            <img 
              src={schoolLogoUrl} 
              alt={school?.name} 
              className="w-16 h-16 object-contain"
            />
          ) : (
            <AppIcon className="w-10 h-10 text-gray-600" />
          )}
        </div>
        <h1 className="text-2xl font-bold">{school?.name || 'School'}</h1>
        <p className="text-lg opacity-90 mt-1">{config.name}</p>
      </div>

      {/* Content */}
      <div className="max-w-md mx-auto px-4 py-6 -mt-4">
        {/* Open App Button */}
        <Card className="shadow-lg mb-6">
          <CardContent className="p-6 text-center">
            <Smartphone className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <h2 className="text-xl font-bold mb-2">Open {config.name}</h2>
            <p className="text-gray-500 mb-4 text-sm">
              Click below to open the app in your browser
            </p>
            <Button onClick={goToApp} className={`w-full ${config.color} hover:opacity-90`} size="lg">
              Open App Now
            </Button>
          </CardContent>
        </Card>

        {/* Add to Home Screen Instructions */}
        <Card className="shadow-lg">
          <CardContent className="p-6">
            <h3 className="font-bold text-lg mb-4 text-center">📱 Add to Home Screen</h3>
            <p className="text-gray-500 text-sm mb-4 text-center">
              For the best experience, add this app to your home screen
            </p>
            
            {isIOS ? (
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 font-bold">1</span>
                  </div>
                  <div>
                    <p className="font-medium">Tap the Share button</p>
                    <p className="text-sm text-gray-500">At the bottom of Safari</p>
                    <Share className="w-5 h-5 mt-1 text-blue-500" />
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 font-bold">2</span>
                  </div>
                  <div>
                    <p className="font-medium">Tap "Add to Home Screen"</p>
                    <p className="text-sm text-gray-500">Scroll down in the menu</p>
                    <PlusSquare className="w-5 h-5 mt-1 text-gray-600" />
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 font-bold">3</span>
                  </div>
                  <div>
                    <p className="font-medium">Tap "Add"</p>
                    <p className="text-sm text-gray-500">In the top right corner</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-green-600 font-bold">1</span>
                  </div>
                  <div>
                    <p className="font-medium">Tap the menu button</p>
                    <p className="text-sm text-gray-500">Three dots at top right</p>
                    <MoreVertical className="w-5 h-5 mt-1 text-gray-600" />
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-green-600 font-bold">2</span>
                  </div>
                  <div>
                    <p className="font-medium">Tap "Add to Home screen"</p>
                    <p className="text-sm text-gray-500">Or "Install app"</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-green-600 font-bold">3</span>
                  </div>
                  <div>
                    <p className="font-medium">Tap "Install" or "Add"</p>
                    <p className="text-sm text-gray-500">Confirm the installation</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-gray-400 text-xs mt-6">
          Cloud Campus • {school?.name}
        </p>
      </div>
    </div>
  );
};

export default AppInstallPage;
